"use client";

import Link from "next/link";
import React, { useRef } from "react";
import { gsap } from "gsap";
import { ArrowRight } from "lucide-react";
import MagneticWrapper from "./MagneticWrapper";

interface ButtonProps {
  children: React.ReactNode;
  href?: string;
  onClick?: () => void;
  type?: "button" | "submit" | "reset";
  variant?: "solid" | "outline" | "text";
  className?: string;
  icon?: boolean;
}

export default function Button({
  children,
  href,
  onClick,
  type = "button",
  variant = "solid",
  className = "",
  icon = true,
}: ButtonProps) {
  const buttonRef = useRef<HTMLButtonElement | HTMLAnchorElement>(null);
  const text1Ref = useRef<HTMLSpanElement>(null);
  const text2Ref = useRef<HTMLSpanElement>(null);
  const iconRef = useRef<HTMLSpanElement>(null);

  const handleMouseEnter = () => {
    // 1. Roll text upward
    gsap.to(text1Ref.current, {
      y: "-110%",
      duration: 0.35,
      ease: "power3.inOut"
    });
    gsap.to(text2Ref.current, {
      y: "0%",
      duration: 0.35,
      ease: "power3.inOut"
    });

    // 2. Elastic liquid border morph (capsule to blob)
    if (variant !== "text") {
      gsap.to(buttonRef.current, {
        borderRadius: "45% 55% 40% 60% / 55% 45% 55% 45%",
        scale: 1.03,
        duration: 0.5,
        ease: "power2.out"
      });
    }

    // 3. Arrow slide reveal
    if (icon && iconRef.current) {
      gsap.to(iconRef.current, {
        x: 4,
        rotate: 45,
        duration: 0.3,
        ease: "power2.out"
      });
    }
  };

  const handleMouseLeave = () => {
    // 1. Reset text roll
    gsap.to(text1Ref.current, {
      y: "0%",
      duration: 0.35,
      ease: "power3.inOut"
    });
    gsap.to(text2Ref.current, {
      y: "110%",
      duration: 0.35,
      ease: "power3.inOut"
    });

    // 2. Revert liquid morph
    if (variant !== "text") {
      gsap.to(buttonRef.current, {
        borderRadius: "9999px",
        scale: 1.0,
        duration: 0.5,
        ease: "elastic.out(1.1, 0.4)"
      });
    }

    // 3. Reset arrow
    if (icon && iconRef.current) {
      gsap.to(iconRef.current, {
        x: 0,
        rotate: 0,
        duration: 0.3,
        ease: "power2.out"
      });
    }
  };

  const baseStyle =
    "relative inline-flex items-center justify-center px-8 py-4 font-sans font-extrabold text-[10px] tracking-[0.22em] uppercase overflow-hidden select-none border transition-colors duration-300";

  const getVariantStyles = () => {
    switch (variant) {
      case "solid":
        return "bg-brand-black border-brand-black text-brand-white hover:bg-brand-red hover:border-brand-red";
      case "outline":
        return "bg-transparent border-brand-black text-brand-black hover:bg-brand-black hover:text-brand-white";
      case "text":
        return "border-transparent bg-transparent text-brand-black hover:text-brand-red px-0 py-2";
    }
  };

  const textLabel = typeof children === "string" ? children : "";

  const content = (
    <span className="relative z-10 flex items-center gap-2.5">
      {/* Scroll text container */}
      <span className="relative block overflow-hidden h-4">
        {/* Row 1: Active label */}
        <span
          ref={text1Ref}
          className="block transition-transform duration-0 select-none"
          style={{ transform: "translateY(0%)", willChange: "transform" }}
        >
          {children}
        </span>
        {/* Row 2: Secondary roll label */}
        {textLabel && (
          <span
            ref={text2Ref}
            className="absolute top-0 left-0 block w-full transition-transform duration-0 select-none text-current"
            style={{ transform: "translateY(110%)", willChange: "transform" }}
          >
            {textLabel}
          </span>
        )}
      </span>
      {icon && variant !== "text" && (
        <span ref={iconRef} className="inline-block transition-transform duration-300">
          <ArrowRight className="w-3.5 h-3.5" />
        </span>
      )}
    </span>
  );

  const buttonClass = `${baseStyle} ${getVariantStyles()} ${className} group rounded-[9999px]`;

  const btnElement = href ? (
    <Link
      href={href}
      ref={buttonRef as React.RefObject<HTMLAnchorElement>}
      className={buttonClass}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {content}
    </Link>
  ) : (
    <button
      ref={buttonRef as React.RefObject<HTMLButtonElement>}
      type={type}
      onClick={onClick}
      className={buttonClass}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {content}
    </button>
  );

  return (
    <MagneticWrapper range={45} strength={0.4}>
      {btnElement}
    </MagneticWrapper>
  );
}
